import { CheckCircle2, ChevronRight, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Price } from "@/components/ui/price";

export type SplitBillShare = {
	id: number | string;
	invoiceNumber: string;
	totalAmount: number;
	paidAmount: number;
	remainingAmount: number;
	paymentStatus: string;
	payments?: Array<{
		id: number | string;
		amount: number;
		paymentMethod: string;
	}>;
};

function statusVariant(status: string): "success" | "warning" | "outline" {
	if (status === "paid") return "success";
	if (status === "partial") return "outline";
	return "warning";
}

export function SplitBillSummaryCard({
	shares,
	currency,
	onOpenShare,
}: {
	shares: SplitBillShare[];
	currency: string;
	onOpenShare: (id: number | string) => void;
}) {
	const { t } = useTranslation();
	const paidCount = shares.filter(share => share.paymentStatus === "paid").length;
	const paidAmount = shares.reduce((sum, share) => sum + Number(share.paidAmount || 0), 0);
	const remainingAmount = shares.reduce((sum, share) => sum + Number(share.remainingAmount || 0), 0);

	return (
		<Card className="border-primary/20">
			<CardHeader>
				<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
					<div>
						<CardTitle className="flex items-center gap-2">
							<Users className="h-5 w-5 text-primary" />
							{t("sales.split.title")}
						</CardTitle>
						<p className="mt-1 text-sm text-muted-foreground">{t("sales.split.description")}</p>
					</div>
					<Badge variant="outline" className="w-fit gap-1 border-primary/25 bg-primary/5 text-primary">
						<CheckCircle2 className="h-3.5 w-3.5" />
						{t("sales.split.progress", { paid: paidCount, total: shares.length })}
					</Badge>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="grid gap-3 rounded-xl bg-muted/50 p-4 sm:grid-cols-3">
					<div>
						<p className="text-xs text-muted-foreground">{t("sales.split.totalShares")}</p>
						<p className="text-xl font-bold">{shares.length}</p>
					</div>
					<div>
						<p className="text-xs text-muted-foreground">{t("sales.detail.paidAmount")}</p>
						<Price value={paidAmount} currency={currency} className="text-xl font-bold text-success" />
					</div>
					<div>
						<p className="text-xs text-muted-foreground">{t("sales.detail.remainingAmount")}</p>
						<Price value={remainingAmount} currency={currency} className="text-xl font-bold text-warning" />
					</div>
				</div>

				<div className="space-y-2">
					{shares.map((share, index) => {
						const status = (share.paymentStatus || "pending").toLowerCase();
						return (
							<button
								key={share.id}
								type="button"
								onClick={() => onOpenShare(share.id)}
								className="group flex w-full items-center gap-3 rounded-xl border p-3 text-start transition-colors hover:border-primary/30 hover:bg-muted/30"
							>
								<span
									className={
										status === "paid"
											? "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success/10 text-success"
											: "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted font-bold text-muted-foreground"
									}
								>
									{status === "paid" ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
								</span>
								<span className="min-w-0 flex-1">
									<span className="flex flex-wrap items-center gap-2">
										<span className="font-semibold">
											{t("sales.split.share", { number: index + 1 })}
										</span>
										<span className="font-mono text-xs text-muted-foreground">
											{share.invoiceNumber}
										</span>
										<Badge variant={statusVariant(status)}>
											{t(`sales.paymentStatuses.${status}`, { defaultValue: status })}
										</Badge>
									</span>
									{share.payments?.length ? (
										<span className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
											{share.payments.map(payment => (
												<span key={payment.id}>
													{t(`sales.paymentMethods.${payment.paymentMethod}`, {
														defaultValue: payment.paymentMethod,
													})}{" "}
													<Price value={Number(payment.amount)} currency={currency} />
												</span>
											))}
										</span>
									) : (
										<span className="mt-1 block text-xs text-muted-foreground">
											{t("sales.split.noPayments")}
										</span>
									)}
								</span>
								<span className="shrink-0 text-end">
									<Price
										value={Number(share.totalAmount)}
										currency={currency}
										className="block font-bold"
									/>
									{Number(share.remainingAmount) > 0 ? (
										<span className="text-xs text-warning">
											{t("sales.split.remaining")}{" "}
											<Price value={Number(share.remainingAmount)} currency={currency} />
										</span>
									) : null}
								</span>
								<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50 group-hover:text-primary rtl:rotate-180" />
							</button>
						);
					})}
				</div>
			</CardContent>
		</Card>
	);
}
